import {
  AgentKeyCreatedSchema,
  MomentSchema,
  AgentKeySchema,
  AssetDetailSchema,
  AssetManifestSchema,
  AssetPageSchema,
  AssetSchema,
  AssetUploadSchema,
  DocumentSchema,
  InviteSchema,
  MessageSchema,
  NoteSchema,
  OAuthGrantSchema,
  ProblemSchema,
  ProjectSchema,
  PublicUserSchema,
  QuestionSchema,
  MembershipStatusSchema,
  MembershipVisibilitySchema,
  SettingsDocSchema,
  ShareCreatedSchema,
  ShareSchema,
  SharedProjectSchema,
  ShortlistEntrySchema,
  SessionSchema,
  SpaceRoleSchema,
  RenditionSchema,
  SpaceSchema,
  SpaceWithRoleSchema,
  TimelineWithSegmentsSchema,
  type AgentKey,
  type AnalysisEngine,
  type AnalysisStage,
  type Asset,
  type AssetDetail,
  type AssetKind,
  type AssetStatus,
  type AssetChange,
  type Document,
  type DocumentDraft,
  type Invite,
  type MemberRoleChange,
  type MemberVisibilityChange,
  type NewAgentKey,
  type Moment,
  type NewMoment,
  type MomentChange,
  type NewShortlistEntry,
  type NewShare,
  type Share,
  type ShortlistEntry,
  type NewAsset,
  type NewInvite,
  type NewMessage,
  type NewNote,
  type NewProject,
  type NewQuestion,
  type NewSpace,
  type Note,
  type OAuthGrant,
  type Question,
  type QuestionUpdate,
  type Rendition,
  type Segment,
  type Session,
  type SegmentChange,
  type SegmentDraft,
  type SettingsDraft,
  type Space,
  type Project,
  type UnfillableStage,
} from "@scenix/core";
import { z } from "zod";

/**
 * Requests go to a relative path. In development Vite proxies /v1 to the Go
 * process; in production studio and API are served from the same origin. Either
 * way the browser sees one host, so the session cookie just works.
 */
const BASE = "/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
  ) {
    super(detail ? `${title}: ${detail}` : title);
    this.name = "ApiError";
  }

  /** The caller is not signed in, or their session expired. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Nothing here, or nothing the caller is allowed to know exists. */
  get isNotFound(): boolean {
    return this.status === 404;
  }

  /**
   * The edit was made against a version the timeline has moved past. The only
   * correct answer is to re-read and reconcile: retrying with a bumped version
   * would overwrite exactly what the check was protecting (ADR 000027).
   */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /**
   * A message worth showing a person. Problem details carry a human-readable
   * detail for exactly this, and falling back to the title keeps the caller
   * from having to check.
   */
  get userMessage(): string {
    return this.detail ?? this.title;
  }
}

const sessionEndedListeners = new Set<() => void>();

/**
 * Watch for a request being refused because the session behind it is gone.
 *
 * Every other refusal belongs beside the control that earned it (ADR 000130).
 * A 401 does not: there is no longer anybody signed in to read it, so it is
 * announced here and the application answers it once, by going back to the
 * sign-in screen (ADR 000131).
 *
 * Returns the unsubscribe, so a caller in a component can hand it straight back
 * from an effect.
 */
export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

/**
 * The error a failed response becomes, in the one place all three fetch paths
 * build it.
 *
 * Exported for the sign-in exchange, which is the one request that does not go
 * through `request`: it is sent before there is a session and its response is
 * a cookie rather than a body. It still gets a problem document when it is
 * refused, and that refusal is the only thing the API can tell somebody who
 * cannot get in (ADR 000109).
 *
 * The shape comes from the generated ProblemSchema rather than a copy written
 * here, so the definition a browser insists on is the one packages/schema holds
 * and Go is verified against (ADR 000020). It is strict, which is the point: a
 * field the API grew without defining is a mismatch somebody has to see, not
 * something to drop quietly.
 *
 * Falling back to the status matters as much. A failure whose body is not a
 * problem document at all is a real thing to meet: a proxy's HTML, an empty
 * 502, a request that never reached the API. There is nothing to read there but
 * the status line, so that is what the error carries.
 */
export async function problemError(response: Response): Promise<ApiError> {
  const problem = ProblemSchema.safeParse(await response.json().catch(() => null));
  const error = problem.success
    ? new ApiError(response.status, problem.data.title, problem.data.detail)
    : new ApiError(response.status, response.statusText || "Request failed");

  /**
   * Announced from here rather than from each caller, because here is where
   * every refusal studio can meet already arrives: the three paths below and
   * the sign-in exchange. A 401 anywhere means the cookie no longer resolves to
   * a person, which is the API's only reason to send one to a browser, so
   * whichever request met it, the session is over (ADR 000131).
   */
  if (error.isUnauthenticated) {
    for (const listener of sessionEndedListeners) listener();
  }

  return error;
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};

  const response = await fetch(`${BASE}${path}`, {
    ...rest,
    // Session lives in an HttpOnly cookie, so it must ride along explicitly.
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...rest.headers,
    },
    ...(json !== undefined ? { body: JSON.stringify(json) } : {}),
  });

  if (!response.ok) {
    throw await problemError(response);
  }

  if (response.status === 204) return schema.parse(undefined);

  /**
   * Responses are validated, not cast. A deployed API drifting from the types
   * the bundle was built against is the normal state of affairs during a
   * rolling deploy; better to fail at the boundary with a readable message than
   * to let an undefined field surface three components deep.
   */
  return schema.parse(await response.json());
}

/**
 * The asset detail response, with the manifest read apart from the rest of it.
 *
 * Everything else in the response is the API's own shape, and a mismatch there
 * is a mismatch somebody has to see (ADR 000020). The manifest is not: it is a
 * document some engine wrote once and every later pass carries forward, so a
 * value this bundle does not define is a fact about that asset's analysis
 * rather than about the API. Parsing it inside the response made one such value
 * cost the whole page (ADR 000113, issue #446).
 */
const AssetDetailEnvelopeSchema = AssetDetailSchema.extend({ manifest: z.unknown() });

/** A detail whose manifest this bundle could not read says so. */
export type AssetDetailRead = AssetDetail & {
  /**
   * Set when the response carried a manifest and it did not parse, which is a
   * different thing to tell somebody than an asset that was never analysed.
   * Absent otherwise, so a reader that only knows about `manifest` is right.
   */
  manifestUnreadable?: boolean;
};

const AssetDetailReadSchema = AssetDetailEnvelopeSchema.transform((envelope): AssetDetailRead => {
  const manifest = AssetManifestSchema.nullable().safeParse(envelope.manifest);
  if (manifest.success) return { ...envelope, manifest: manifest.data };

  // Nothing else will mention it: the page renders, reports that it rendered,
  // and leaves the manifest where it is. The same reasoning as ADR 000090's
  // skipped document, on the side of the wire where a person is watching.
  console.error(
    `asset ${envelope.asset.id}: its manifest holds something this build cannot read`,
    manifest.error,
  );
  return { ...envelope, manifest: null, manifestUnreadable: true };
});

const HealthSchema = z.object({
  status: z.literal("ok"),
  version: z.string(),
});

const MemberSchema = z.object({
  user: PublicUserSchema,
  role: SpaceRoleSchema,
  visibility: MembershipVisibilitySchema,
  visibleProjectIds: z.array(z.string()),
  // A membership is never deleted, so a listing carries people who have left
  // as well as people who are here (ADR 000059).
  status: MembershipStatusSchema,
  deactivatedAt: z.string().nullish(),
});

export type Member = z.infer<typeof MemberSchema>;

/** Narrows a corpus listing. Every field is optional. */
export interface AssetFilter {
  kind?: AssetKind;
  status?: AssetStatus;
  limit?: number;
  /** Show the recovery window instead of ordinary use (issue #8). */
  deleted?: boolean;
  /** Matches manifest content: transcript, summary and topics, not just the name. */
  q?: string;
  /**
   * Resume where a page stopped: the `nextCursor` a previous listing answered
   * with (ADR 000093). Everything else has to stay the same between pages,
   * because each of the fields above narrows the listing being walked.
   */
  after?: Asset["id"];
}

/**
 * The steps of the analysis pipeline a caller can name (ADR 000063), and the
 * body of an asset's page (ADR 000073), re-exported from the generated schemas
 * so the routes that use them keep importing from one place.
 *
 * Both come from packages/schema like everything else here. They were written
 * out by hand beside the generated ones for a while, which meant a fifth stage
 * added in Go would have been refused by a browser that had never heard of it
 * (issue #314).
 */
export type { AnalysisEngine, AnalysisStage, AssetDetail, UnfillableStage };

/**
 * A segment on its way to the server, as the shared schema defines it: a length
 * and a brief. The state, the current rendition and the timeline are the
 * server's to decide (ADR 000025).
 */
export type DraftSegment = SegmentDraft;

/**
 * Which frame a thumbnail URL is asking for, taken from the storage path the
 * manifest holds (ADR 000072).
 *
 * The object's name carries a fingerprint of its bytes, so the filename is
 * already an answer to "which picture is this": a pass that pulls a different
 * frame writes it under a different name, and a pass that pulls the same one
 * writes it under the same name and leaves every cache warm.
 *
 * The whole filename rather than the fingerprint parsed out of it, so nothing
 * here knows how a still is named. A still written before that decision is
 * `thumb-0`, which is stable for as long as those bytes are the ones the
 * manifest names, and that is all this has to be.
 */
function stillVersion(path: string): string {
  const name = path.slice(path.lastIndexOf("/") + 1);
  const dot = name.lastIndexOf(".");
  return encodeURIComponent(dot > 0 ? name.slice(0, dot) : name);
}

/**
 * One page of a space's corpus (ADR 000093).
 *
 * Outside the object below because both `listAssets` and `listAssetPage` are
 * this call, differing only in how much of the answer they hand back.
 */
function fetchAssetPage(spaceId: Space["id"], filter?: AssetFilter) {
  const query = new URLSearchParams();
  if (filter?.kind) query.set("kind", filter.kind);
  if (filter?.status) query.set("status", filter.status);
  if (filter?.limit) query.set("limit", String(filter.limit));
  if (filter?.deleted) query.set("deleted", "true");
  if (filter?.q) query.set("q", filter.q);
  if (filter?.after) query.set("after", filter.after);
  const suffix = query.size > 0 ? `?${query}` : "";
  return request(`/spaces/${spaceId}/assets${suffix}`, AssetPageSchema);
}

export const api = {
  health: () => request("/healthz", HealthSchema),

  currentUser: () => request("/me", PublicUserSchema),
  signOut: () => request("/auth/session", z.undefined(), { method: "DELETE" }),

  listSpaces: () => request("/spaces", z.array(SpaceWithRoleSchema)),

  createSpace: (draft: NewSpace) =>
    request("/spaces", SpaceWithRoleSchema, { method: "POST", json: draft }),

  getSpace: (spaceId: Space["id"]) => request(`/spaces/${spaceId}`, SpaceWithRoleSchema),

  listMembers: (spaceId: Space["id"]) =>
    request(`/spaces/${spaceId}/members`, z.array(MemberSchema)),

  /**
   * A space's settings: what is always true of it, sent to a model in full
   * (ADR 000037). `deleted: true` shows the recovery window instead of
   * ordinary use (issue #8).
   */
  listSettings: (spaceId: Space["id"], opts?: { deleted?: boolean }) =>
    request(
      `/spaces/${spaceId}/settings${opts?.deleted ? "?deleted=true" : ""}`,
      z.array(SettingsDocSchema),
    ),

  getSettings: (spaceId: Space["id"], slug: string) =>
    request(`/spaces/${spaceId}/settings/${slug}`, SettingsDocSchema),

  /** Creates or replaces. A stale `baseRevision` is a 409, not a silent overwrite. */
  putSettings: (spaceId: Space["id"], slug: string, draft: SettingsDraft) =>
    request(`/spaces/${spaceId}/settings/${slug}`, SettingsDocSchema, {
      method: "PUT",
      json: draft,
    }),

  /**
   * Moments: authored observations about ranges of an asset (ADR 000051).
   *
   * The asset detail already carries them, so this is for the cases that want
   * them on their own, and for refetching after a write.
   */
  listMoments: (spaceId: Space["id"], assetId: Asset["id"], opts?: { deleted?: boolean }) =>
    request(
      `/spaces/${spaceId}/assets/${assetId}/moments${opts?.deleted ? "?deleted=true" : ""}`,
      z.array(MomentSchema),
    ),

  createMoment: (spaceId: Space["id"], assetId: Asset["id"], draft: NewMoment) =>
    request(`/spaces/${spaceId}/assets/${assetId}/moments`, MomentSchema, {
      method: "POST",
      json: draft,
    }),

  /** Corrects one. Absent fields are left alone. */
  updateMoment: (
    spaceId: Space["id"],
    assetId: Asset["id"],
    momentId: Moment["id"],
    change: MomentChange,
  ) =>
    request(`/spaces/${spaceId}/assets/${assetId}/moments/${momentId}`, MomentSchema, {
      method: "PATCH",
      json: change,
    }),

  /**
   * Stores a still for a moment. Raw bytes, like a logo: this is one frame at
   * thumbnail size, and where it lives is the server's to decide (ADR 000051).
   */
  putMomentThumbnail: async (
    spaceId: Space["id"],
    assetId: Asset["id"],
    momentId: Moment["id"],
    still: Blob,
  ): Promise<Moment> => {
    const response = await fetch(
      `${BASE}/spaces/${spaceId}/assets/${assetId}/moments/${momentId}/thumbnail`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": still.type || "image/jpeg" },
        body: still,
      },
    );
    if (!response.ok) {
      throw await problemError(response);
    }
    return MomentSchema.parse(await response.json());
  },

  /** Marks it gone rather than removing it, so it can be restored (ADR 000055). */
  deleteMoment: (spaceId: Space["id"], assetId: Asset["id"], momentId: Moment["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/moments/${momentId}`, z.undefined(), {
      method: "DELETE",
    }),

  /** Undoes a delete inside the recovery window (ADR 000055). */
  restoreMoment: (spaceId: Space["id"], assetId: Asset["id"], momentId: Moment["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/moments/${momentId}/restore`, MomentSchema, {
      method: "POST",
    }),

  /**
   * The shortlist: moments gathered for a project (ADR 000051).
   *
   * Entries name a moment rather than copying it and are resolved on read, so
   * a moment corrected on the asset reads correctly everywhere it was sent.
   * One whose moment has since been deleted is filtered by the server.
   */
  listShortlist: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/shortlist`, z.array(ShortlistEntrySchema)),

  /** Sending the same moment twice is idempotent. */
  addToShortlist: (
    spaceId: Space["id"],
    projectId: Project["id"],
    entry: NewShortlistEntry,
  ): Promise<ShortlistEntry> =>
    request(`/spaces/${spaceId}/projects/${projectId}/shortlist`, ShortlistEntrySchema, {
      method: "POST",
      json: entry,
    }),

  removeFromShortlist: (
    spaceId: Space["id"],
    projectId: Project["id"],
    assetId: Asset["id"],
    momentId: Moment["id"],
  ) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/shortlist/${assetId}/${momentId}`,
      z.undefined(),
      { method: "DELETE" },
    ),

  /**
   * Appends a gathered moment to the timeline as a planned segment, sourced
   * from its own asset and range. Returns the whole timeline, because the
   * write is version-checked and the caller needs the version it landed at.
   */
  promoteShortlistEntry: (
    spaceId: Space["id"],
    projectId: Project["id"],
    assetId: Asset["id"],
    momentId: Moment["id"],
  ) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/shortlist/${assetId}/${momentId}/promote`,
      TimelineWithSegmentsSchema,
      { method: "POST" },
    ),

  /**
   * Corrects what a person wrote about an asset: its name, and where it came
   * from (ADR 000060). What the system measured is not editable.
   */
  updateAsset: (spaceId: Space["id"], assetId: Asset["id"], change: AssetChange) =>
    request(`/spaces/${spaceId}/assets/${assetId}`, AssetSchema, {
      method: "PATCH",
      json: change,
    }),

  /** Marks it gone rather than removing it, so it can be restored (issue #8). */
  deleteSettings: (spaceId: Space["id"], slug: string) =>
    request(`/spaces/${spaceId}/settings/${slug}`, z.undefined(), { method: "DELETE" }),

  /** Undoes a delete inside the recovery window (issue #8). */
  restoreSettings: (spaceId: Space["id"], slug: string) =>
    request(`/spaces/${spaceId}/settings/${slug}/restore`, SettingsDocSchema, { method: "POST" }),

  /** Admin only: house governance, not corpus work (ADR 000036). */
  updateMemberRole: (
    spaceId: Space["id"],
    userId: Member["user"]["id"],
    change: MemberRoleChange,
  ) =>
    request(`/spaces/${spaceId}/members/${userId}`, MemberSchema, {
      method: "PATCH",
      json: change,
    }),

  /** Admin only: narrows or widens which projects a member may see (ADR 000046). */
  updateMemberVisibility: (
    spaceId: Space["id"],
    userId: Member["user"]["id"],
    change: MemberVisibilityChange,
  ) =>
    request(`/spaces/${spaceId}/members/${userId}/visibility`, MemberSchema, {
      method: "PATCH",
      json: change,
    }),

  /**
   * Ends somebody's access. The membership is kept, so they stay in the
   * listing marked inactive and can be brought back (ADR 000059).
   */
  removeMember: (spaceId: Space["id"], userId: Member["user"]["id"]) =>
    request(`/spaces/${spaceId}/members/${userId}`, z.undefined(), { method: "DELETE" }),

  /** Gives access back, on the same membership rather than a new one. */
  reactivateMember: (spaceId: Space["id"], userId: Member["user"]["id"]) =>
    request(`/spaces/${spaceId}/members/${userId}/reactivate`, MemberSchema, { method: "POST" }),

  /** Everything a space has issued, pending or not, newest first. */
  listInvites: (spaceId: Space["id"]) =>
    request(`/spaces/${spaceId}/invites`, z.array(InviteSchema)),

  createInvite: (spaceId: Space["id"], draft: NewInvite) =>
    request(`/spaces/${spaceId}/invites`, InviteSchema, { method: "POST", json: draft }),

  revokeInvite: (spaceId: Space["id"], inviteId: Invite["id"]) =>
    request(`/spaces/${spaceId}/invites/${inviteId}`, z.undefined(), { method: "DELETE" }),

  /**
   * Where a space's logo lives, for an `<img src>`. Not a signed URL: the API
   * serves the bytes itself, so this is just the path, and the browser's
   * session cookie is what authorizes reading it.
   */
  spaceLogoUrl: (spaceId: Space["id"]) => `${BASE}/spaces/${spaceId}/logo`,

  /**
   * Uploads or replaces a space's logo. Raw bytes, not JSON: the body is the
   * image itself, sniffed and validated by the API rather than trusted from
   * the file's own claimed type.
   */
  setSpaceLogo: async (spaceId: Space["id"], file: File): Promise<Space> => {
    const response = await fetch(`${BASE}/spaces/${spaceId}/logo`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": file.type || "application/octet-stream" },
      body: file,
    });
    if (!response.ok) {
      throw await problemError(response);
    }
    return SpaceSchema.parse(await response.json());
  },

  deleteSpaceLogo: (spaceId: Space["id"]) =>
    request(`/spaces/${spaceId}/logo`, z.undefined(), { method: "DELETE" }),

  /**
   * Rename a space. The slug it was founded with does not move, and no agent
   * key may call this at all: renaming is a person's, through the interface
   * (ADR 000050).
   */
  renameSpace: (spaceId: Space["id"], name: string) =>
    request(`/spaces/${spaceId}`, SpaceSchema, { method: "PATCH", json: { name } }),

  /**
   * Spaces this account owns that are in the recovery window: the one place a
   * removed space is visible, and only to somebody who could put it back
   * (ADR 000062).
   */
  listRemovedSpaces: () => request("/spaces?deleted=true", z.array(SpaceWithRoleSchema)),

  /**
   * Remove a space. Owner only, and only from here: no agent key and no MCP
   * client can remove one at all.
   *
   * Nothing beneath it is touched. It becomes reachable by nobody, through
   * every door at once, and thirty days later the sweep empties the tree for
   * good (ADR 000062).
   */
  removeSpace: (spaceId: Space["id"]) =>
    request(`/spaces/${spaceId}`, z.undefined(), { method: "DELETE" }),

  /** Take a space back out of the recovery window, whole. */
  restoreSpace: (spaceId: Space["id"]) =>
    request(`/spaces/${spaceId}/restore`, SpaceSchema, { method: "POST" }),

  /**
   * One of a space's three project listings (ADR 000079). Nothing asked for is
   * the live ones; `deleted` is the other side of the recovery window, which is
   * what the restore listing shows (ADR 000078); `archived` is the retired
   * ones. A project that is both is on the deleted side alone, which is why
   * asking for both gives that.
   */
  listProjects: (spaceId: Space["id"], options?: { deleted?: boolean; archived?: boolean }) => {
    const query = options?.deleted ? "?deleted=true" : options?.archived ? "?archived=true" : "";
    return request(`/spaces/${spaceId}/projects${query}`, z.array(ProjectSchema));
  },

  createProject: (spaceId: Space["id"], draft: NewProject) =>
    request(`/spaces/${spaceId}/projects`, ProjectSchema, { method: "POST", json: draft }),

  getProject: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}`, ProjectSchema),

  /**
   * Rename a project, change its status, move its stage, or any combination.
   * Archiving is the retire path: an archived project keeps its treatment,
   * timeline and notes, and moves from the space's project list to its archived
   * one, which is also where it comes back from (ADR 000079). Stage moves in
   * either direction with no ratchet (ADR 000021).
   */
  updateProject: (
    spaceId: Space["id"],
    projectId: Project["id"],
    change: { name?: string; status?: Project["status"]; stage?: Project["stage"] },
  ) =>
    request(`/spaces/${spaceId}/projects/${projectId}`, ProjectSchema, {
      method: "PATCH",
      json: change,
    }),

  /**
   * Remove a project. Nothing beneath it is touched: the treatment, timeline,
   * notes and thread all stay exactly as they were and stop answering, and
   * thirty days later the sweep empties the subtree for good (ADR 000078).
   *
   * The footage is not the project's, so this takes the plan and leaves every
   * asset and every moment where they are (ADR 000008).
   */
  removeProject: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}`, z.undefined(), { method: "DELETE" }),

  /** Take a project back out of the recovery window, whole. */
  restoreProject: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/restore`, ProjectSchema, { method: "POST" }),

  /**
   * The corpus in a space: names, kinds, summaries and topics for everything at
   * once. This is what you read to decide where to look, and it never carries a
   * manifest (ADR 000028).
   */
  listAssets: (spaceId: Space["id"], filter?: AssetFilter) =>
    fetchAssetPage(spaceId, filter).then((page) => page.assets),

  /**
   * The same listing, with the place it stopped (ADR 000093).
   *
   * A space accumulates and the listing is capped, so anything that means to
   * reach the whole corpus rather than glance at the newest of it calls this
   * one and walks `nextCursor` until it comes back empty. `listAssets` above is
   * the first page and nothing more, which is all a palette or a picker wants.
   */
  listAssetPage: (spaceId: Space["id"], filter?: AssetFilter) => fetchAssetPage(spaceId, filter),

  /**
   * Bring content in. Returns the asset and, for an upload, a signed URL to PUT
   * the bytes to; media never passes through the API.
   */
  createAsset: (spaceId: Space["id"], draft: NewAsset) =>
    request(`/spaces/${spaceId}/assets`, AssetUploadSchema, { method: "POST", json: draft }),

  /** Say the bytes arrived, which hands the asset to analysis. */
  completeUpload: (spaceId: Space["id"], assetId: Asset["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/complete`, AssetSchema, { method: "POST" }),

  getAsset: (spaceId: Space["id"], assetId: Asset["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}`, AssetDetailReadSchema),

  /**
   * Give a failed asset back to the worker, rather than leaving delete and
   * re-upload as the only way past a transient failure (issue #68).
   */
  retryAnalysis: (spaceId: Space["id"], assetId: Asset["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/retry`, AssetSchema, { method: "POST" }),

  /**
   * Run analysis over an asset that is working, filling what is missing and
   * leaving everything else as it is (ADR 000063, issue #141).
   *
   * Distinct from retryAnalysis, which is for one that failed and has nothing
   * to lose. Naming stages in `replace` overwrites what they produced, so the
   * argument is omitted rather than defaulted: a request that destroys
   * something has to have said so.
   */
  reanalyzeAsset: (spaceId: Space["id"], assetId: Asset["id"], replace?: AnalysisStage[]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/reanalyze`, AssetSchema, {
      method: "POST",
      ...(replace?.length ? { json: { replace } } : {}),
    }),

  /** Marks it gone rather than removing it, so it can be restored (issue #8). */
  deleteAsset: (spaceId: Space["id"], assetId: Asset["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}`, z.undefined(), { method: "DELETE" }),

  /** Undoes a delete inside the recovery window (issue #8). */
  restoreAsset: (spaceId: Space["id"], assetId: Asset["id"]) =>
    request(`/spaces/${spaceId}/assets/${assetId}/restore`, AssetSchema, { method: "POST" }),

  /**
   * Where a video's cover still lives, for an `<img src>`. Not a signed URL:
   * the API serves the bytes itself (issue #3), the same reasoning as
   * spaceLogoUrl above, so a listing of many assets costs no IAM signing call
   * per row.
   *
   * Takes the storage path the caller is already holding, so the URL can carry
   * which frame it is asking for (stillVersion, ADR 000072). Without it the
   * address is the same before and after a re-analysis, and the week of
   * immutable caching that keeps a listing free shows the previous pass's
   * frames instead of the ones somebody paid for (issue #249).
   */
  assetThumbnailUrl: (spaceId: Space["id"], assetId: Asset["id"], path: string) =>
    `${BASE}/spaces/${spaceId}/assets/${assetId}/thumbnail?v=${stillVersion(path)}`,

  /** A moment's still, served the same way an asset's thumbnail is. */
  momentThumbnailUrl: (spaceId: Space["id"], assetId: Asset["id"], momentId: Moment["id"]) =>
    `${BASE}/spaces/${spaceId}/assets/${assetId}/moments/${momentId}/thumbnail`,

  /** One of the other stills the ingest worker pulled, for a filmstrip. */
  assetThumbnailAtUrl: (spaceId: Space["id"], assetId: Asset["id"], index: number, path: string) =>
    `${BASE}/spaces/${spaceId}/assets/${assetId}/thumbnails/${index}?v=${stillVersion(path)}`,

  /**
   * One of the stills standing in for a segment's picture (ADR 000070).
   *
   * By its place in the brief rather than by the path the brief holds: a
   * segment's stills are storage paths, and a route that took one would serve
   * back any object in the bucket a caller cared to name.
   */
  segmentStillUrl: (
    spaceId: Space["id"],
    projectId: Project["id"],
    segmentId: Segment["id"],
    index: number,
  ) =>
    `${BASE}/spaces/${spaceId}/projects/${projectId}/timeline/segments/${segmentId}/stills/${index}`,

  /**
   * Agent keys: how a non-human caller authenticates (ADR 000030).
   *
   * Every key the account holds, across spaces, so there is one place to see
   * what exists and stop it when a laptop goes missing (ADR 000041).
   */
  listAgentKeys: () => request("/agent-keys", z.array(AgentKeySchema)),

  /**
   * Mint one, for the space it will work in. That space is the key's, and the
   * only one it can reach (ADR 000041).
   *
   * The secret is in this response and nowhere else, ever again: only its hash
   * is stored.
   */
  createAgentKey: (spaceId: Space["id"], draft: NewAgentKey) =>
    request(`/spaces/${spaceId}/agent-keys`, AgentKeyCreatedSchema, {
      method: "POST",
      json: draft,
    }),

  /** Stop a key working, without touching the owner's session. */
  revokeAgentKey: (keyId: AgentKey["id"]) =>
    request(`/agent-keys/${keyId}`, z.undefined(), { method: "DELETE" }),

  /**
   * Connectors: every OAuth client this account has authorized, across every
   * space (ADR 000043). An authorization outlives the tab it was granted in,
   * so this is the one place to see what is connected and stop it.
   */
  listConnectors: () => request("/connectors", z.array(OAuthGrantSchema)),

  /** Revoke a connector's standing authorization. It stops on its next call. */
  revokeConnector: (grantId: OAuthGrant["id"]) =>
    request(`/connectors/${grantId}`, z.undefined(), { method: "DELETE" }),

  /**
   * Where this account is signed in (ADR 000086). A session outlives the
   * machine it was made on, so this is the one place a person can see the
   * others and end them.
   */
  listSessions: () => request("/sessions", z.array(SessionSchema)),

  /** End one session. A request carrying it is refused from here on. */
  endSession: (sessionId: Session["id"]) =>
    request(`/sessions/${sessionId}`, z.undefined(), { method: "DELETE" }),

  /** End every session but this one, so the browser asking stays signed in. */
  endOtherSessions: () => request("/sessions", z.undefined(), { method: "DELETE" }),

  /**
   * A project's documents. `deleted: true` shows the recovery window instead
   * of ordinary use (ADR 000076).
   */
  listDocuments: (spaceId: Space["id"], projectId: Project["id"], opts?: { deleted?: boolean }) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/documents${opts?.deleted ? "?deleted=true" : ""}`,
      z.array(DocumentSchema),
    ),

  /**
   * Adds a document to the project — a script, an outline, a shot list, a
   * note. The treatment is created alongside the project itself, not here
   * (ADR 000011).
   */
  createDocument: (spaceId: Space["id"], projectId: Project["id"], draft: DocumentDraft) =>
    request(`/spaces/${spaceId}/projects/${projectId}/documents`, DocumentSchema, {
      method: "POST",
      json: draft,
    }),

  /**
   * The revision-checked write path (ADR 000011, ADR 000027): baseRevision is
   * the revision this edit was made against, and a stale one comes back as a
   * 409 the caller re-reads and reconciles rather than silently loses.
   */
  updateDocument: (
    spaceId: Space["id"],
    projectId: Project["id"],
    documentId: Document["id"],
    change: DocumentDraft,
  ) =>
    request(`/spaces/${spaceId}/projects/${projectId}/documents/${documentId}`, DocumentSchema, {
      method: "PUT",
      json: change,
    }),

  /**
   * Marks it gone rather than removing it, so it can be restored (ADR 000076).
   * The treatment is refused with a 409, since every reader starts from it.
   */
  deleteDocument: (spaceId: Space["id"], projectId: Project["id"], documentId: Document["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/documents/${documentId}`, z.undefined(), {
      method: "DELETE",
    }),

  /** Undoes a delete inside the recovery window, at the revision it left at. */
  restoreDocument: (spaceId: Space["id"], projectId: Project["id"], documentId: Document["id"]) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/documents/${documentId}/restore`,
      DocumentSchema,
      { method: "POST" },
    ),

  getTimeline: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/timeline`, TimelineWithSegmentsSchema),

  /**
   * The whole set, not a patch. An edit shifts the timing of everything after
   * it, and "segments do not overlap" can only be checked against the complete
   * set, so the API takes all of them (ADR 000025).
   *
   * Segments being written for the first time have no id; the server mints one.
   */
  replaceSegments: (
    spaceId: Space["id"],
    projectId: Project["id"],
    segments: readonly DraftSegment[],
    expectedVersion?: number,
  ) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/timeline/segments`,
      TimelineWithSegmentsSchema,
      {
        method: "PUT",
        json: { segments, ...(expectedVersion === undefined ? {} : { expectedVersion }) },
      },
    ),

  /**
   * Change one segment without sending the rest. What an agent uses for almost
   * everything, and what studio uses when only one segment moved (ADR 000027).
   *
   * The whole chain is still validated server-side, because moving one segment
   * can overlap its neighbour. What the caller is spared is transmitting it.
   */
  updateSegment: (
    spaceId: Space["id"],
    projectId: Project["id"],
    segmentId: Segment["id"],
    change: SegmentChange,
  ) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/timeline/segments/${segmentId}`,
      TimelineWithSegmentsSchema,
      { method: "PATCH", json: change },
    ),

  /**
   * Takes one still off a segment, by its place in the brief (ADR 000071).
   *
   * A verb of its own because no brief write can do it: one that sends no
   * stills keeps the ones the segment had, deliberately, so that a client which
   * has never heard of them cannot drop them by round-tripping a segment
   * through an edit (ADR 000070).
   *
   * The version goes in the address rather than a body, because a DELETE has
   * none. It answers with the chain, the way every write to the chain does.
   */
  removeSegmentStill: (
    spaceId: Space["id"],
    projectId: Project["id"],
    segmentId: Segment["id"],
    index: number,
    expectedVersion?: number,
  ) =>
    request(
      `/spaces/${spaceId}/projects/${projectId}/timeline/segments/${segmentId}/stills/${index}` +
        (expectedVersion === undefined ? "" : `?expectedVersion=${expectedVersion}`),
      TimelineWithSegmentsSchema,
      { method: "DELETE" },
    ),

  listNotes: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/notes`, z.array(NoteSchema)),

  /**
   * Asks for a segment's picture to be made (ADR 000056).
   *
   * The rendition it returns is the queue entry, not the finished file: the
   * worker claims it, cuts the brief's sources and writes the result back, and
   * the segment's state follows the rendition it points at. A render already
   * made at the same fidelity from the same brief comes back as it is rather
   * than being made twice, which is what the fingerprint is for.
   *
   * Only `rough` is built, and only per segment. Anything else is refused with
   * the reason rather than silently served a rough cut.
   */
  requestRender: (
    spaceId: Space["id"],
    projectId: Project["id"],
    segmentId: Segment["id"],
    fidelity: Rendition["fidelity"] = "rough",
  ) =>
    request(`/spaces/${spaceId}/projects/${projectId}/timeline/renditions`, RenditionSchema, {
      method: "POST",
      json: { segmentId, fidelity },
    }),

  /**
   * Shares: a read-only link to one project, for somebody with no account
   * (ADR 000057).
   *
   * The token comes back once, on the mint, and is never in a listing: a
   * listing that carried working links would make every reader of it an
   * audience. What a listing carries is a label and a hint, which is enough to
   * decide which one to revoke.
   */
  listShares: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/shares`, z.array(ShareSchema)),

  createShare: (spaceId: Space["id"], projectId: Project["id"], draft: NewShare) =>
    request(`/spaces/${spaceId}/projects/${projectId}/shares`, ShareCreatedSchema, {
      method: "POST",
      json: draft,
    }),

  /**
   * Stops a link working, immediately and permanently. A link is forwardable
   * and a forwarded one cannot be taken back from whoever also received it, so
   * this is the only real control.
   */
  revokeShare: (spaceId: Space["id"], projectId: Project["id"], shareId: Share["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/shares/${shareId}`, z.undefined(), {
      method: "DELETE",
    }),

  /**
   * Opens a share. The one call in this client that carries no session: the
   * audience for a share has no account, which is what it exists for.
   */
  openShare: (token: string) => request(`/shares/${token}`, SharedProjectSchema),

  /**
   * Asks for the whole chain as one file (ADR 000061).
   *
   * Concatenates the pictures that exist rather than cutting sources again, so
   * it is cheap to ask for and refused outright when the chain has a hole in
   * it: assembling short would hand somebody a video the timeline disagrees
   * with. A 409 carries which segments to render first.
   */
  requestAssembly: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/timeline/renditions`, RenditionSchema, {
      method: "POST",
      json: { fidelity: "rough" },
    }),

  createNote: (spaceId: Space["id"], projectId: Project["id"], draft: NewNote) =>
    request(`/spaces/${spaceId}/projects/${projectId}/notes`, NoteSchema, {
      method: "POST",
      json: draft,
    }),

  updateNote: (
    spaceId: Space["id"],
    projectId: Project["id"],
    noteId: Note["id"],
    change: { status: Note["status"]; resolutionNote?: string },
  ) =>
    request(`/spaces/${spaceId}/projects/${projectId}/notes/${noteId}`, NoteSchema, {
      method: "PATCH",
      json: change,
    }),

  listMessages: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/thread/messages`, z.array(MessageSchema)),

  /**
   * Naming answersQuestionId doubles as answering: the question moves to
   * answered in the same write, because the message is the proposal
   * (ADR 000012).
   */
  postMessage: (spaceId: Space["id"], projectId: Project["id"], draft: NewMessage) =>
    request(`/spaces/${spaceId}/projects/${projectId}/thread/messages`, MessageSchema, {
      method: "POST",
      json: draft,
    }),

  listQuestions: (spaceId: Space["id"], projectId: Project["id"]) =>
    request(`/spaces/${spaceId}/projects/${projectId}/thread/questions`, z.array(QuestionSchema)),

  /** Raising a question posts its own origin message in the same write. */
  askQuestion: (spaceId: Space["id"], projectId: Project["id"], draft: NewQuestion) =>
    request(`/spaces/${spaceId}/projects/${projectId}/thread/questions`, QuestionSchema, {
      method: "POST",
      json: draft,
    }),

  /** Resolve, dismiss, escalate or reopen. Resolving needs an answer. */
  updateQuestion: (
    spaceId: Space["id"],
    projectId: Project["id"],
    change: QuestionUpdate & { id: Question["id"] },
  ) => {
    const { id, ...update } = change;
    return request(
      `/spaces/${spaceId}/projects/${projectId}/thread/questions/${id}`,
      QuestionSchema,
      {
        method: "PATCH",
        json: update,
      },
    );
  },
} as const;
